import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import supplierService from '../../../services/supplierService';

const CapacityManagementModal = ({ supplier, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    maxCapacity: supplier?.capacity?.maxCapacity || 0,
    currentWorkload: supplier?.capacity?.currentWorkload || 0,
    deliveryTimeAvg: supplier?.capacity?.deliveryTimeAvg || 0,
    qualityScore: supplier?.capacity?.qualityScore || 0,
    onTimeDeliveryRate: supplier?.capacity?.onTimeDeliveryRate || 0,
    geographicCoverage: supplier?.capacity?.geographicCoverage?.join(', ') || ''
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      await supplierService?.updateSupplierCapacity(supplier?.id, {
        maxCapacity: parseInt(formData?.maxCapacity) || 0,
        currentWorkload: parseInt(formData?.currentWorkload) || 0,
        deliveryTimeAvg: parseInt(formData?.deliveryTimeAvg) || 0,
        qualityScore: parseFloat(formData?.qualityScore) || 0,
        onTimeDeliveryRate: parseFloat(formData?.onTimeDeliveryRate) || 0,
        geographicCoverage: formData?.geographicCoverage
          ?.split(',')
          ?.map(c => c?.trim())
          ?.filter(Boolean) || []
      });

      onSuccess();
    } catch (err) {
      console.error('Error updating capacity:', err);
      setError('Erreur lors de la mise à jour de la capacité');
    } finally {
      setLoading(false);
    }
  };

  const utilizationRate = formData?.maxCapacity > 0
    ? ((formData?.currentWorkload / formData?.maxCapacity) * 100)?.toFixed(1)
    : 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Gestion de la Capacité</h2>
            <p className="text-sm text-gray-600 mt-1">
              {supplier?.companyName || supplier?.fullName}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Capacity Metrics */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Métriques de Capacité</h3>
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Capacité Maximale"
                type="number"
                value={formData?.maxCapacity}
                onChange={(e) => handleChange('maxCapacity', e?.target?.value)}
                placeholder="100"
              />
              <Input
                label="Charge Actuelle"
                type="number"
                value={formData?.currentWorkload}
                onChange={(e) => handleChange('currentWorkload', e?.target?.value)}
                placeholder="50"
              />
            </div>

            {/* Utilization Display */}
            <div className="mt-4 p-4 bg-blue-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Taux d'Utilisation</span>
                <span className="text-lg font-bold text-blue-600">{utilizationRate}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all"
                  style={{ width: `${Math.min(utilizationRate, 100)}%` }}
                />
              </div>
            </div>
          </div>

          {/* Performance Metrics */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Métriques de Performance</h3>
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Délai Moyen (jours)"
                type="number"
                value={formData?.deliveryTimeAvg}
                onChange={(e) => handleChange('deliveryTimeAvg', e?.target?.value)}
                placeholder="7"
              />
              <Input
                label="Score Qualité (0-5)"
                type="number"
                step="0.1"
                min="0"
                max="5"
                value={formData?.qualityScore}
                onChange={(e) => handleChange('qualityScore', e?.target?.value)}
                placeholder="4.5"
              />
              <Input
                label="Taux Livraison à Temps (%)"
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={formData?.onTimeDeliveryRate}
                onChange={(e) => handleChange('onTimeDeliveryRate', e?.target?.value)}
                placeholder="95.0"
              />
            </div>
          </div>

          {/* Geographic Coverage */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Couverture Géographique</h3>
            <Input
              label="Pays/Régions (séparés par des virgules)"
              value={formData?.geographicCoverage}
              onChange={(e) => handleChange('geographicCoverage', e?.target?.value)}
              placeholder="France, Belgique, Suisse"
            />
            <p className="text-xs text-gray-500 mt-1">
              Entrez les pays ou régions desservis, séparés par des virgules
            </p>
          </div>

          {/* Machine Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Informations Machines</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-500">Total Machines</p>
                <p className="text-lg font-semibold text-gray-900">
                  {supplier?.capacity?.totalMachines || 0}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Machines Actives</p>
                <p className="text-lg font-semibold text-green-600">
                  {supplier?.capacity?.activeMachines || 0}
                </p>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Les machines sont automatiquement comptabilisées depuis le parc machines
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
              <Icon name="AlertCircle" size={20} className="text-red-600 flex-shrink-0" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CapacityManagementModal;